import { useQuery } from "@tanstack/react-query";

import { axios } from "@/client/libs/axios";
import { useDynamicCountryDetection } from "@/client/hooks/use-dynamic-country-detection";

type PaymentConfig = {
  stripe: {
    enabled: boolean;
  };
  paddle: {
    enabled: boolean;
  };
  fastspring: {
    enabled: boolean;
  };
  preferredProvider: string | null;
  paymentsEnabled: boolean;
};

type GatewayRoute = {
  country: string;
  primary: string;
  fallbacks: string[];
  notes?: string;
};

export const usePaymentConfig = () => {
  return useQuery<PaymentConfig>({
    queryKey: ["payment", "config"],
    queryFn: async () => {
      const response = await axios.get<PaymentConfig>("/payment/config");
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
};

/**
 * Fetches country-based gateway routing recommendation.
 * Uses detected country code to get the recommended payment provider.
 */
export const useGatewayRoute = () => {
  const { countryCode, loading: countryLoading } = useDynamicCountryDetection();

  const query = useQuery<GatewayRoute>({
    queryKey: ["payment", "gateway-config", countryCode],
    queryFn: async () => {
      const response = await axios.get<GatewayRoute>(
        `/payment/gateway-config?country=${countryCode}`,
      );
      return response.data;
    },
    enabled: !countryLoading && !!countryCode,
    staleTime: 1000 * 60 * 30, // 30 min — routing rarely changes
    gcTime: 1000 * 60 * 60,
  });

  return {
    ...query,
    countryCode,
    countryLoading,
  };
};
